import React, { FC, useState } from "react";
import { LuChevronRight, LuPencil, LuPlus } from "react-icons/lu";
import { useNavigate, useParams } from "react-router-dom";

import { Card } from "../../../../../../../../components/card/Card";
import { LottieLoading } from "../../../../../../../../components/graphics/LottieLoading";
import { Button } from "../../../../../../../../components/newUI/button";
import { getLinkedFirm } from "../../../../../../../../sessionStorage/sessionStorage";
import { useGetContactCustomersNotesQuery } from "../../../../../../../../slices/ContactNotesSlice";
import { useDeleteOrganizationNoteMutation } from "../../../../../../../../slices/OrganizationNotesSlice";
import { useDeleteWorkNoteMutation } from "../../../../../../../../slices/WorkNotesSlice";
import {
  IOrganizationContact,
  IOrganizationContactNote,
  IWorkNote,
} from "../../../../../../../../types/types";
import {
  getNoteAuthorColor,
  getNoteAuthorName,
  mapNoteToViewModel,
  newestNotes,
  NotePreviewRow,
  useCanEditNote,
  useNoteDeleteConfirm,
} from "../../../../../../components/notes";
import { NoteViewModal } from "../../../../../../components/NoteViewModal";
import { ProfileWidgetEmptyState } from "../../../../../../components/profile";
import ErrorPopup from "../../../../../../modals/ErrorPopup";
import { OrganizationNoteModal } from "../../../../../../modals/OrganizationNoteModal";

type Props = {
  organization?: IOrganizationContact;
};

export const OrganizationNotesWidget: FC<Props> = ({ organization }) => {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const orgId = getLinkedFirm()?.orgId || "";
  const customerId = organization?.customerId || "";

  // Same list as the Notes tab (`search-notes`): the organization's own notes
  // AND its work items' notes, so "Across all work for this client" is true.
  const { data: allNotes, isFetching } = useGetContactCustomersNotesQuery(
    { orgId, customerId, query: "" },
    { skip: !customerId || !orgId },
  );
  const notes = newestNotes(
    allNotes as unknown as IOrganizationContactNote[] | undefined,
  );
  const [deleteOrganizationNote] = useDeleteOrganizationNoteMutation();
  const [deleteWorkNote] = useDeleteWorkNoteMutation();
  // Each row's own id is `noteId`; work notes are deleted through the work.
  const deleteNote = async (note: IOrganizationContactNote) => {
    const id = note.noteId || note.id;
    try {
      if (note.workId) {
        await deleteWorkNote({ id, workId: note.workId } as IWorkNote).unwrap();
      } else {
        await deleteOrganizationNote({ ...note, id }).unwrap();
      }
    } catch {
      setIsOpenError(true);
    }
  };
  const { requestDelete, confirmModal: confirmDeleteModal } =
    useNoteDeleteConfirm<IOrganizationContactNote>(
      deleteNote,
      (note) => note.title,
    );

  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [isOpenError, setIsOpenError] = useState(false);
  const [currentNote, setCurrentNote] =
    useState<IOrganizationContactNote | null>(null);
  const [viewNote, setViewNote] = useState<IOrganizationContactNote | null>(
    null,
  );
  const canEdit = useCanEditNote();
  const [isWork, setIsWork] = useState(false);
  const handleOpenAddModal = () => {
    setIsOpenAdd(true);
    setIsWork(false);
    setCurrentNote(null);
  };

  return (
    <>
      <Card extraClasses={"overflow-hidden"}>
        <div className={"flex h-[386px] flex-col p-[22px]"}>
          <div
            className={"mb-4 flex shrink-0 items-start justify-between gap-4"}>
            <span className={"flex flex-col gap-0.5"}>
              <span className={"text-[17px] font-extrabold text-[#1F2433]"}>
                Notes
              </span>
              <span className={"text-[11.5px] font-semibold text-[#9097A8]"}>
                Across all work for this client
              </span>
            </span>
            <span className={"flex shrink-0 items-center gap-3"}>
              <Button
                type={"button"}
                variant={"ghost"}
                size={"sm"}
                autoWidth
                rightIcon={<LuChevronRight size={14} strokeWidth={2.2} />}
                className={
                  "h-auto p-0 text-[13px] font-bold text-[#6B4EFF] hover:bg-transparent"
                }
                onClick={() =>
                  navigate(`/organization/${organizationId}/notes`)
                }>
                View all
              </Button>
              <Button
                type={"button"}
                variant={"outline"}
                size={"sm"}
                iconOnly
                autoWidth
                leftIcon={<LuPlus size={16} strokeWidth={2.2} />}
                className={
                  "h-8 w-8 rounded-lg border-[#E4E7EC] p-0 text-[#6B4EFF] hover:bg-[#F4F2FF]"
                }
                onClick={handleOpenAddModal}
              />
            </span>
          </div>
          <div className={"flex min-h-0 flex-1 flex-col"}>
            {isFetching ? (
              <div className={"p-4"}>
                <LottieLoading />
              </div>
            ) : !notes?.length ? (
              <ProfileWidgetEmptyState
                icon={<LuPencil size={28} strokeWidth={1.7} />}
                title={"No notes"}
              />
            ) : (
              <div
                className={
                  "mr-[-22px] min-h-0 flex-1 overflow-y-auto pr-[22px]"
                }>
                {notes?.map((note) => (
                  <NotePreviewRow
                    key={note?.noteId || note?.id}
                    note={mapNoteToViewModel(note)}
                    onView={() => setViewNote(note)}
                    onEdit={
                      !canEdit(note)
                        ? undefined
                        : () => {
                            setCurrentNote(note);
                            setIsWork(!!note.workName);
                            setIsOpenAdd(true);
                          }
                    }
                    onDelete={() => requestDelete(note)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </Card>
      {isOpenAdd && (
        <OrganizationNoteModal
          isActive={isOpenAdd}
          isWork={isWork}
          onClose={() => setIsOpenAdd(false)}
          defaultValues={currentNote || undefined}
        />
      )}
      {viewNote && (
        <NoteViewModal
          note={{
            id: viewNote.noteId || viewNote.id,
            title: viewNote.title,
            workId: viewNote.workId,
            workName: viewNote.workName,
            note: viewNote.note,
            createdAt: viewNote.createdAt,
            authorName: getNoteAuthorName(viewNote),
            authorColor: getNoteAuthorColor(viewNote),
          }}
          onClose={() => setViewNote(null)}
          onEdit={
            !canEdit(viewNote)
              ? undefined
              : () => {
                  setCurrentNote(viewNote);
                  setIsWork(!!viewNote.workName);
                  setViewNote(null);
                  setIsOpenAdd(true);
                }
          }
        />
      )}
      <ErrorPopup
        closePopup={() => setIsOpenError(false)}
        isOpen={isOpenError}
      />
      {confirmDeleteModal}
    </>
  );
};
